import { Button, Checkbox, Textarea } from "@mantine/core";
import { useState, type ChangeEvent } from "react";
import { useToast } from "../../../../mantine/notifications/useToast";
import type { TodoNote } from "../../../../utils/backend-types";
import { WhichUserModal } from "../WhichUserModal/WhichUserModal";
import type { NoteAddMutation, NoteMutation } from "./Notes.types";

interface NotesEntryProps {
  note?: TodoNote;
  isStaffUser: boolean;
  submitButtonText: string;
  onSaveNote: NoteAddMutation | NoteMutation;
  onSaveNoteDone?: (note: TodoNote) => void;
  onCancel?: () => void;
}

function NotesEntry({
  note,
  isStaffUser,
  submitButtonText,
  onSaveNote,
  onSaveNoteDone,
  onCancel
}: NotesEntryProps) {
  const { showErrorToast } = useToast();
  const [text, setText] = useState(note?.note ?? "");
  const [internalChecked, setInternalChecked] = useState(
    note?.internal ?? false
  );
  const [submitting, setSubmitting] = useState(false);
  const [showSystemUserQuestionModal, setShowSystemUserQuestionModal] =
    useState(false);

  function handleChangeText(e: ChangeEvent<HTMLTextAreaElement>) {
    setText(e.target.value);
  }

  function handleClickSave() {
    const isEditing = !!note?.internal;

    setSubmitting(true);

    if (isStaffUser && !isEditing) {
      setShowSystemUserQuestionModal(true);
    } else {
      saveNote();
    }
  }

  function handleChooseWhichUser(asSystemUser: boolean) {
    setShowSystemUserQuestionModal(false);
    saveNote(asSystemUser);
  }

  function saveNote(asSystemUser?: boolean) {
    const promise = note
      ? (onSaveNote as NoteMutation)({
          note_id: note.id,
          note: text,
          internal: internalChecked
        })
      : (onSaveNote as NoteAddMutation)({
          note: text,
          internal: internalChecked,
          as_system_user: asSystemUser
        });
    promise
      .then((newNote) => {
        if (onSaveNoteDone) onSaveNoteDone(newNote.data);
        setText("");
        setInternalChecked(false);
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  return (
    <div className="Notes-entry">
      <Textarea
        id="todo-notes-entry"
        mb="sm"
        name="todo-notes-entry"
        placeholder="Notiz hinzufügen"
        value={text}
        onChange={handleChangeText}
      />
      {isStaffUser && (
        <Checkbox
          checked={internalChecked}
          label="Interne Notiz"
          onChange={(event) => setInternalChecked(event.currentTarget.checked)}
        />
      )}
      <div>
        <Button
          color="primary"
          disabled={text.length === 0}
          loading={submitting}
          onClick={handleClickSave}
        >
          {submitButtonText}
        </Button>
        {onCancel && (
          <Button
            style={{ marginLeft: "3px" }}
            variant="default"
            onClick={onCancel}
          >
            Abbrechen
          </Button>
        )}
      </div>
      <WhichUserModal
        isOpen={showSystemUserQuestionModal}
        questionText="Wie soll die Notiz erstellt werden?"
        toggle={() =>
          setShowSystemUserQuestionModal(!showSystemUserQuestionModal)
        }
        onClickSelf={() => handleChooseWhichUser(false)}
        onClickSystemUser={() => handleChooseWhichUser(true)}
      />
    </div>
  );
}

export { NotesEntry };
